import { Button, Divider, Group, Stack, Switch, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { Right } from 'components/index';
import { i18nKeys, useTranslation } from 'locales';
import type { User } from 'shared/hooks/use-load-users';
import { type UpdateUserForm, useUpdateUser } from 'shared/hooks/use-update-user';
import { useGetConstants } from 'shared/utils/selectors';

type EditCollaboratorFormProps = { initialValues: User };

export const EditCollaboratorForm = ({ initialValues }: EditCollaboratorFormProps) => {
  const { t } = useTranslation();
  const constants = useGetConstants();

  const { updateUser } = useUpdateUser();
  const form = useForm<UpdateUserForm>({ initialValues });

  const handleEditUser = (values: UpdateUserForm) => {
    updateUser({ id: initialValues.id, data: values });
    modals.closeAll();
  };

  const isAdmin = form.values.permissions?.admin;
  const isActive = form.values.isActive;

  return (
    <form onSubmit={form.onSubmit(handleEditUser)}>
      <Group align="start" px="md">
        <Stack w="fit-content">
          <Title order={6}>{t(i18nKeys.FORM.EDIT_STAFF.TITLE)}</Title>
          <Switch
            {...form.getInputProps('isActive', { type: 'checkbox' })}
            label={t(i18nKeys.SETTINGS.STAFF.ACTIVE)}
          />
          {isActive && (
            // biome-ignore lint/complexity/noUselessFragments: It isn't.
            <>
              {constants.user_roles.map(item => {
                if (item.value !== 'admin' && isAdmin) return null;
                return (
                  <Switch
                    key={item.value}
                    {...form.getInputProps(`permissions.${item.value}`, { type: 'checkbox' })}
                    label={item.description}
                  />
                );
              })}
            </>
          )}
        </Stack>
        <Divider mx="md" orientation="vertical" />
        <Stack w="fit-content">
          <Title order={6}>{t(i18nKeys.SETTINGS.STAFF.RECEIVE_EMAIL)}</Title>
          <Switch
            {...form.getInputProps('preferences.emails.daily', { type: 'checkbox' })}
            label={t(i18nKeys.SETTINGS.STAFF.DAILY)}
          />
          <Switch
            {...form.getInputProps('preferences.emails.weekly', { type: 'checkbox' })}
            label={t(i18nKeys.SETTINGS.STAFF.WEEKLY)}
          />
          <Switch
            {...form.getInputProps('preferences.emails.notifications', { type: 'checkbox' })}
            label={t(i18nKeys.SETTINGS.STAFF.NOTIFICATION)}
          />
          <Switch
            {...form.getInputProps('preferences.emails.tasks', { type: 'checkbox' })}
            label={t(i18nKeys.SETTINGS.STAFF.TASK)}
          />
        </Stack>
      </Group>
      <Right mt="md">
        <Button disabled={!form.isDirty()} type="submit">
          {t(i18nKeys.SAVE)}
        </Button>
      </Right>
    </form>
  );
};
